import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Loader2,
  AlertCircle,
  Maximize,
  Minimize,
  LogOut,
  RefreshCw,
} from 'lucide-react';
import DashboardBlockCard from '@/components/dashboard-builder/DashboardBlockCard';
import ControlBar from '@/components/dashboard-builder/controls/ControlBar';
import { useDashboardViewer } from '@/hooks/useDashboardViewer';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { DASHBOARD_TOKENS } from '@/components/dashboard-builder/rendering/tokens';
import type { RenderHandle } from '@/types/dashboard';

const DashboardCastPage: React.FC = () => {
  const { id: dashboardId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    dashboard,
    blocks,
    blockData,
    controls,
    activeControlValues,
    isLoading,
    isRefreshing,
    error,
    renderHandles,
    changeControlValue,
    refreshData,
    lastRefreshedAt,
    isPolling,
  } = useDashboardViewer(dashboardId);

  const { isIdle } = useIdleTimer(5000);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isLive = dashboard?.data_mode === 'live';
  const relativeTime = useRelativeTime(lastRefreshedAt);

  const castControls = useMemo(
    () => controls.filter((c) => c.show_in_cast_mode),
    [controls]
  );

  const handleRenderHandle = useCallback(
    (handle: RenderHandle) => {
      renderHandles.current.set(handle.blockId, handle);
    },
    [renderHandles]
  );

  const handleDisposeHandle = useCallback(
    (blockId: string) => {
      renderHandles.current.delete(blockId);
    },
    [renderHandles]
  );

  const exitCastMode = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    navigate(`/dashboards/${dashboardId}`);
  }, [navigate, dashboardId]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // Fullscreen may not be available in all contexts
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = (): void => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        exitCastMode();
      } else if (e.key === 'f' || e.key === 'F') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [exitCastMode, toggleFullscreen]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="text-center">
          <Loader2
            className="w-10 h-10 animate-spin mx-auto mb-3"
            style={{ color: DASHBOARD_TOKENS.textSecondary }}
          />
          <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: DASHBOARD_TOKENS.textPrimary }}
          >
            Failed to load dashboard
          </h2>
          <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm mb-4">
            {error || 'Dashboard not found'}
          </p>
          <button
            onClick={exitCastMode}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary"
          >
            <LogOut className="w-4 h-4" />
            Exit Cast Mode
          </button>
        </div>
      </div>
    );
  }

  const layoutColumns = dashboard.layout_columns ?? 2;
  const hasBlocks = blocks.length > 0;

  return (
    <div
      className="dark flex flex-col h-screen overflow-hidden select-none"
      style={{
        backgroundColor: DASHBOARD_TOKENS.bgPrimary,
        cursor: isIdle ? 'none' : 'default',
      }}
    >
      {/* Top overlay: title + live indicator + refresh */}
      <div
        className={clsx(
          'flex-shrink-0 px-5 py-2.5 flex items-center gap-3 transition-opacity duration-500',
          isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
        )}
        style={{ backgroundColor: DASHBOARD_TOKENS.bgHeader }}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <h1
            className="text-sm font-medium truncate"
            style={{ color: DASHBOARD_TOKENS.textSecondary }}
          >
            {dashboard.name}
          </h1>

          {isLive && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-success/10 text-success border border-success/20 flex-shrink-0">
              <span
                className={clsx(
                  'w-1.5 h-1.5 rounded-full bg-success',
                  isPolling && 'animate-pulse'
                )}
              />
              Live
            </span>
          )}
        </div>

        <div className="flex items-center gap-2.5 flex-shrink-0">
          {relativeTime && (
            <span
              className="text-[11px] tabular-nums"
              style={{ color: DASHBOARD_TOKENS.textMuted }}
            >
              Updated {relativeTime}
            </span>
          )}

          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors hover:bg-white/5 disabled:opacity-40"
            style={{ color: DASHBOARD_TOKENS.textSecondary }}
            title="Refresh data"
          >
            <RefreshCw
              className={clsx('w-3.5 h-3.5', isRefreshing && 'animate-spin')}
            />
          </button>
        </div>
      </div>

      {/* Control bar (cast-mode controls only) */}
      {castControls.length > 0 && (
        <div
          className={clsx(
            'transition-opacity duration-500',
            isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
          )}
        >
          <ControlBar
            controls={castControls}
            activeControlValues={activeControlValues}
            isRefreshing={isRefreshing}
            onChangeControlValue={changeControlValue}
          />
        </div>
      )}

      {/* Block grid */}
      <div className="flex-1 overflow-y-auto">
        {hasBlocks ? (
          <div
            className="p-4 gap-4"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${layoutColumns}, 1fr)`,
              alignContent: 'start',
            }}
          >
            {blocks.map((block) => {
              const data = blockData.get(block.id) ?? [];
              const spanW = Math.min(block.position.w, layoutColumns);

              return (
                <div key={block.id} style={{ gridColumn: `span ${spanW}` }}>
                  <DashboardBlockCard
                    block={block}
                    data={data}
                    viewOnly
                    onRenderHandle={handleRenderHandle}
                    onDisposeHandle={handleDisposeHandle}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-sm px-4">
              <AlertCircle
                className="w-12 h-12 mx-auto mb-4"
                style={{ color: DASHBOARD_TOKENS.textMuted }}
              />
              <h3
                className="text-lg font-medium mb-2"
                style={{ color: DASHBOARD_TOKENS.textSecondary }}
              >
                No charts to display
              </h3>
              <p
                className="text-sm mb-4"
                style={{ color: DASHBOARD_TOKENS.textMuted }}
              >
                This dashboard has no accepted blocks yet.
              </p>
              <button
                onClick={exitCastMode}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary"
              >
                Exit Cast Mode
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom-right overlay: fullscreen + exit */}
      <div
        className={clsx(
          'fixed bottom-4 right-4 flex items-center gap-2 transition-opacity duration-500',
          isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
        )}
      >
        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-lg transition-colors hover:bg-white/10"
          style={{ color: DASHBOARD_TOKENS.textSecondary }}
          title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
        >
          {isFullscreen ? (
            <Minimize className="w-4 h-4" />
          ) : (
            <Maximize className="w-4 h-4" />
          )}
        </button>

        <button
          onClick={exitCastMode}
          className="p-2 rounded-lg transition-colors hover:bg-white/10"
          style={{ color: DASHBOARD_TOKENS.textSecondary }}
          title="Exit Cast Mode (Esc)"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default DashboardCastPage;
